// The viewer's scene passes render into their own multisampled target, and
// the post chain after them runs on single-sample targets.
//
// EffectComposer clones its first target for the ping-pong pair, so with
// MSAA on both are multisampled and every fullscreen post pass (the bloom
// stabiliser, the bloom composite, the cinematic grade, SMAA) wrote four
// samples a pixel and paid a resolve, for a quad that covers every sample
// the same. Here the passes before the first post pass that swaps buffers
// (the app's RenderPass and the close view's cloud pass) are sent to
// sceneRT, that post pass reads sceneRT's resolved texture, and the pair
// drops to one sample. Every fullscreen pass writes the same value it did,
// so the image is unchanged.
//
// Rocketry does the same in its own budget (rocketry/budget.js) while it is
// up, reading the composer's sample count when it enters; uninstall() puts
// the pair back first, and sync() re-installs once rocketry has gone.
// window.__viewerSceneTargetOff = true leaves the composer as it was (A/B).
import * as THREE from 'three';

export function createViewerSceneTarget() {
  let composer = null;
  let sceneRT = null;
  let samples = 0;
  let unwrap = [];
  let builtCount = -1;
  let builtPost = null;
  // A composer this could not be installed on, and its pass count then, so
  // it is not retried every frame until its passes change.
  let refused = null;
  let refusedCount = -1;

  function firstSwapIndex(list) {
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      if (p.enabled !== false && p.needsSwap) return i;
    }
    return -1;
  }

  // Keep sceneRT the size of the chain it feeds; checked on every scene pass
  // so a capture that resizes the composer and renders directly is covered.
  function sizeTo(buffer) {
    if (sceneRT && buffer && (sceneRT.width !== buffer.width || sceneRT.height !== buffer.height)) {
      sceneRT.setSize(buffer.width, buffer.height);
    }
  }

  function undoRedirect() {
    for (const fn of unwrap) fn();
    unwrap = [];
    builtCount = -1;
    builtPost = null;
  }

  // False when there is no post pass to hand over to: the scene would then
  // land in a single-sample buffer, so the caller puts the samples back.
  function redirect() {
    undoRedirect();
    const list = composer.passes;
    const i = firstSwapIndex(list);
    if (i <= 0) return false;
    for (const p of list.slice(0, i)) {
      const orig = p.render;
      p.render = function (rend, writeBuffer, readBuffer, dt, mask) {
        sizeTo(readBuffer);
        return orig.call(this, rend, writeBuffer, sceneRT, dt, mask);
      };
      unwrap.push(() => { p.render = orig; });
    }
    const post = list[i];
    const origPost = post.render;
    post.render = function (rend, writeBuffer, readBuffer, dt, mask) {
      return origPost.call(this, rend, writeBuffer, sceneRT, dt, mask);
    };
    unwrap.push(() => { post.render = origPost; });
    builtCount = list.length;
    builtPost = post;
    return true;
  }

  function uninstall() {
    undoRedirect();
    if (sceneRT) {
      sceneRT.dispose();
      sceneRT = null;
    }
    if (composer && samples) {
      for (const t of [composer.renderTarget1, composer.renderTarget2]) {
        if (!t) continue;
        t.samples = samples;
        t.dispose();
      }
    }
    composer = null;
    samples = 0;
  }

  function install(next) {
    const rt = next.renderTarget1;
    const n = rt ? (rt.samples || 0) : 0;
    if (!n) return;
    composer = next;
    samples = n;
    sceneRT = new THREE.WebGLRenderTarget(rt.width, rt.height, {
      type: rt.texture.type,
      format: rt.texture.format,
      minFilter: rt.texture.minFilter,
      magFilter: rt.texture.magFilter,
      colorSpace: rt.texture.colorSpace,
      depthBuffer: true,
      stencilBuffer: rt.stencilBuffer,
      samples: n
    });
    sceneRT.texture.name = 'viewer.scene';
    for (const t of [composer.renderTarget1, composer.renderTarget2]) {
      if (!t) continue;
      t.samples = 0;
      t.dispose();
    }
    if (!redirect()) {
      uninstall();
      refused = next;
      refusedCount = next.passes.length;
    }
  }

  // Once a frame, before composer.render(), while the viewer owns the composer.
  function sync(next) {
    const off = typeof window !== 'undefined' && window.__viewerSceneTargetOff;
    if (off || !next) {
      if (composer) uninstall();
      return;
    }
    if (composer !== next) {
      if (composer) uninstall();
      if (refused === next && refusedCount === next.passes.length) return;
      refused = null;
      install(next);
      return;
    }
    // A pass inserted or removed (the close view's cloud pass), or the first
    // post pass switched on or off.
    const list = composer.passes;
    const i = firstSwapIndex(list);
    if (list.length !== builtCount || list[i] !== builtPost) {
      if (!redirect()) {
        refused = composer;
        refusedCount = list.length;
        uninstall();
        return;
      }
    }
    sizeTo(composer.readBuffer);
  }

  return { sync, uninstall, get active() { return !!sceneRT; } };
}
